import React, { useEffect, useState } from 'react';
import ReportNote from '../components/ReportNote';
import { localDateStr } from './utils';

const API = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

const BAND_COLOR = { red: 'var(--critical)', amber: 'var(--warn)', green: 'var(--ok)', overload: 'var(--accent)' };

function FillBar({ pct, band }) {
  const color = BAND_COLOR[band] || 'var(--text-3)';
  const width = Math.min(pct, 130) / 130 * 100;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 6, background: 'var(--border)', borderRadius: 99, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${width}%`, background: color, borderRadius: 99, transition: 'width .4s' }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 700, color, minWidth: 38, textAlign: 'right' }}>{pct}%</span>
    </div>
  );
}


export default function TeamKpiReport() {
  const [period, setPeriod] = useState('week');
  const [anchor, setAnchor] = useState(localDateStr(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = (p, a) => {
    setLoading(true);
    fetch(`${API}/api/reports/team-kpi?period=${p}&anchor=${a}`)
      .then(r => r.json()).then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(period, anchor); }, [period, anchor]);

  const changePeriod = (p) => {
    setAnchor(localDateStr(new Date()));
    setPeriod(p);
  };

  const changeAnchor = (delta) => {
    const [y, m, day] = anchor.split('-').map(Number);
    const d = new Date(y, m - 1, day);
    if (period === 'week') d.setDate(d.getDate() + delta * 7);
    else if (period === 'month') d.setMonth(d.getMonth() + delta, 1);
    else if (period === 'year') d.setFullYear(d.getFullYear() + delta, 0, 1);
    else d.setDate(d.getDate() + delta * 30);
    setAnchor(localDateStr(d));
  };

  const exportCSV = () => {
    window.location.href = `${API}/api/reports/export/csv?type=team-kpi&period=${period}&anchor=${anchor}`;
  };

  const printReport = () => window.print();

  return (
    <div>
      <ReportNote what="Daily time-based KPIs: does each person have enough logged work?" period={data ? data.label : ''} />
      <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {['week', 'month', 'rolling30', 'year'].map(p => (
            <button key={p} onClick={() => changePeriod(p)}
              className={`btn ${period === p ? 'btn-primary' : 'btn-secondary'}`} style={{ padding: '6px 12px' }}>
              {p === 'week' ? 'This week' : p === 'month' ? 'This month' : p === 'rolling30' ? 'Rolling 30 days' : 'This year'}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={() => changeAnchor(-1)} className="btn btn-secondary" style={{ padding: '6px 12px' }}>‹</button>
          <span style={{ fontWeight: 700, fontSize: 15, minWidth: 180, textAlign: 'center' }}>
            {data ? data.label : anchor}
          </span>
          <button onClick={() => changeAnchor(1)} className="btn btn-secondary" style={{ padding: '6px 12px' }}>›</button>
        </div>
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <button onClick={exportCSV} className="btn btn-secondary" style={{ gap: 6 }}>↓ CSV</button>
          <button onClick={printReport} className="btn btn-secondary" style={{ gap: 6 }}>🖨 Print / PDF</button>
        </div>
      </div>

      {loading && <div className="loading">Loading team KPI data…</div>}

      {!loading && data && (
        <>
          <div className="grid-4" style={{ marginBottom: 24 }}>
            <div className="card">
              <div className="card-title">Team Fill Rate</div>
              <div className="card-value" style={{ color: BAND_COLOR[data.team.band] }}>{data.team.fill_rate}%</div>
              <div className="card-sub">{data.team.productive_display} / {data.team.target_display}</div>
            </div>
            <div className="card">
              <div className="card-title">Band Split</div>
              <div className="card-value" style={{ fontSize: 15 }}>
                <span style={{ color: BAND_COLOR.red }}>{data.team.bands.red}</span>
                {' · '}
                <span style={{ color: BAND_COLOR.amber }}>{data.team.bands.amber}</span>
                {' · '}
                <span style={{ color: BAND_COLOR.green }}>{data.team.bands.green}</span>
                {' · '}
                <span style={{ color: BAND_COLOR.overload }}>{data.team.bands.overload}</span>
              </div>
              <div className="card-sub">Low · Watch · Healthy · Overload</div>
            </div>
            <div className="card">
              <div className="card-title">Spare Capacity</div>
              <div className="card-value">{data.team.spare_fte_days}</div>
              <div className="card-sub">FTE-days, people below 90% fill</div>
            </div>
            <div className="card">
              <div className="card-title">Team Members</div>
              <div className="card-value">{data.team.member_count}</div>
              <div className="card-sub">tracked in this report</div>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 24 }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 18, fontFamily: 'Syne, sans-serif' }}>
              Per-Person KPIs — {data.label}
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th style={{ textAlign: 'right' }}>Target</th>
                    <th style={{ textAlign: 'right' }}>Productive</th>
                    <th style={{ textAlign: 'right' }}>Billable</th>
                    <th style={{ textAlign: 'right' }}>Absence</th>
                    <th style={{ textAlign: 'right' }}>ABE</th>
                    <th style={{ textAlign: 'right' }}>Lost %</th>
                    <th style={{ minWidth: 160 }}>Fill Rate</th>
                    <th style={{ textAlign: 'right' }}>Days Logged</th>
                    <th style={{ textAlign: 'right' }}>Gap Days</th>
                    <th style={{ textAlign: 'right' }}>Discipline</th>
                    <th style={{ textAlign: 'center' }}>Bulk-logged</th>
                  </tr>
                </thead>
                <tbody>
                  {data.members.map(m => (
                    <tr key={m.name}>
                      <td style={{ fontWeight: 600 }}>{m.name}</td>
                      <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', color: 'var(--text-3)' }}>{m.target_display}</td>
                      <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', color: 'var(--accent)' }}>{m.productive_display}</td>
                      <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', color: 'var(--ok)' }}>{m.billable_display}</td>
                      <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', color: m.absence_seconds ? 'var(--warn)' : 'var(--text-3)' }}>{m.absence_seconds ? m.absence_display : '—'}</td>
                      <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', color: 'var(--text-3)' }}>{m.abe_seconds ? m.abe_display : '—'}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: m.lost_pct >= 30 ? 'var(--critical)' : m.lost_pct >= 15 ? 'var(--warn)' : 'var(--text-3)' }}>{m.lost_pct}%</td>
                      <td style={{ minWidth: 160 }}><FillBar pct={m.fill_rate} band={m.band} /></td>
                      <td style={{ textAlign: 'right' }}>{m.days_logged}/{m.working_days}</td>
                      <td style={{ textAlign: 'right', color: m.gap_days ? 'var(--warn)' : 'var(--text-3)' }}>{m.gap_days || '—'}</td>
                      <td style={{ textAlign: 'right' }}>{m.logging_discipline}%</td>
                      <td style={{ textAlign: 'center' }}>{m.bulk_logging_flag ? '⚠' : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ marginTop: 14, fontSize: 11, color: 'var(--text-3)', borderTop: '1px solid var(--border)', paddingTop: 10, display: 'flex', gap: 20, flexWrap: 'wrap' }}>
              <span>Fill Rate = productive time ÷ target (Target is already net of absence)</span>
              <span>Billable = time on tickets with the Billable checkbox ticked (excl. Absence/ABE)</span>
              <span>Lost % = (ABE + Non-Billable) ÷ time actually worked (excl. Absence)</span>
              <span>Discipline = % of working days with ≥6h logged</span>
              <span>⚠ Bulk-logged = one day held &gt;50% of a week's hours</span>
              <span>Absence days count as neither logged nor a gap</span>
              <span>ABE = internal Artemys Belgium project time, excluded from Target and Fill Rate</span>
            </div>
          </div>
        </>
      )}

      <style>{`
        @media print {
          .no-print { display: none !important; }
          .sidebar, .topbar { display: none !important; }
          .main { margin: 0 !important; padding: 20px !important; }
        }
      `}</style>
    </div>
  );
}
